import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import get from 'lodash/get';
import { useTranslation } from '../../src/i18n';
import { TP, PROFESSIONS_IDS, EDIT_SEASON_MW_STEP, EDIT_SEASON_MEDIA_STEP } from '../../constants/consts';
import ListText from '../ListText';
import TitledBlock from '../TitledBlock';
import { getFilteredTourDates } from '../../utils/common';
import ProductionCardCollapsedContent from '../ProductionCardCollapsedContent';
import EditProductionCardLayout from '../EditComponents/EditProductionCardLayout';

const useStyles = makeStyles(() => ({
  contentInfoTitles: {
    display: 'flex',
  },
  content: {
    padding: '0.6rem',
  },
}));

const EditProductionCard = ({
  className,
  production,
  onCancel,
  updatedProduction,
  editProductionLinkProps,
  onPublish,
  onDelete,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [productionToShow, setProduction] = React.useState(production);
  // eslint-disable-next-line camelcase
  const [conductors] = React.useState(
    production?.crew.filter(member => member?.profession?.id === PROFESSIONS_IDS.CONDUCTOR),
  );
  const [directors] = React.useState(
    production?.crew.filter(member => member?.profession?.id === PROFESSIONS_IDS.STAGE_DIRECTOR),
  );

  React.useEffect(() => {
    if (updatedProduction && updatedProduction?.id === production?.id) {
      setProduction(updatedProduction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedProduction]);

  const getVenuesFromDates = React.useCallback(() => {
    const uniqVenues = {};
    const uniqDates = {};
    (productionToShow?.dates || []).forEach(date => {
      const venueId = date?.venue?.id;
      if (!uniqVenues[venueId] && venueId) {
        uniqVenues[venueId] = date?.venue;
      }
      const calendarDate = date?.date;
      if (!uniqDates[calendarDate] && calendarDate) {
        uniqDates[calendarDate] = date;
      }
    });
    return { venues: Object.values(uniqVenues), dates: Object.values(uniqDates) };
  }, [productionToShow]);

  return (
    <>
      <EditProductionCardLayout
        className={className}
        production={{ ...productionToShow, dates: get(getVenuesFromDates(), 'dates', []) }}
        editLinkProps={editProductionLinkProps(EDIT_SEASON_MW_STEP)}
        mediaLinkProps={editProductionLinkProps(EDIT_SEASON_MEDIA_STEP)}
        onDeleteClick={onDelete}
        onPublishClick={onPublish}
        onCancelClick={onCancel}
        infoBlock={
          <>
            <div className={classes.contentInfoTitles}>
              <TitledBlock title={t(`${TP}.m_CONDUCTOR`)}>
                <ListText list={conductors} display="column" fontWeight="bold" fieldToDisplay="profile.name" />
              </TitledBlock>
              <TitledBlock title={t(`${TP}.m_DIR`)}>
                <ListText list={directors} display="column" fontWeight="bold" fieldToDisplay="profile.name" />
              </TitledBlock>
            </div>
            <ProductionCardCollapsedContent
              other={productionToShow?.other}
              cast={productionToShow?.casts}
              crew={productionToShow?.crew}
              tourDates={getFilteredTourDates(productionToShow?.dates)}
              showEmpty
              showText
            />
          </>
        }
      />
    </>
  );
};

EditProductionCard.propTypes = {
  className: PropTypes.string,
  production: PropTypes.object.isRequired,
  onCancel: PropTypes.func,
  onPublish: PropTypes.func,
  onDelete: PropTypes.func,
  updatedProduction: PropTypes.object,
  editProductionLinkProps: PropTypes.func.isRequired,
};

EditProductionCard.defaultProps = {
  className: '',
  updatedProduction: null,
  onPublish: () => {},
  onDelete: () => {},
};

export default memo(EditProductionCard);
